/**
 * PAPPO — MoodCheckIn ("Como você tá?")
 * Card de check-in de humor da criança, no topo da lista de conversas.
 * Enquanto não responde, pulsa chamando atenção; ao responder, mostra uma
 * confirmação carinhosa e encolhe num chip (que dá pra tocar pra trocar).
 */

import { useState, useEffect, useCallback } from 'react';
import { getTodayMood, setMood as apiSetMood, type MoodKey } from '../../services/mood';
import styles from './MoodCheckIn.module.css';

const MOODS: { key: MoodKey; emoji: string; label: string; msg: string }[] = [
  { key: 'feliz', emoji: '😄', label: 'Feliz', msg: 'Que bom que você tá feliz hoje! 💛' },
  { key: 'bem', emoji: '🙂', label: 'Bem', msg: 'Que bom que você tá bem! 💛' },
  {
    key: 'mais_ou_menos',
    emoji: '😐',
    label: 'Mais ou menos',
    msg: 'Tá tudo bem ter dias assim. 💛 Seus pais estão aqui se você precisar.',
  },
  {
    key: 'triste',
    emoji: '😢',
    label: 'Triste',
    msg: 'Sinto muito que você tá triste. 💛 Que tal conversar com seus pais?',
  },
  {
    key: 'bravo',
    emoji: '😠',
    label: 'Bravo',
    msg: 'Tá tudo bem ficar bravo às vezes. 💛 Seus pais estão aqui pra te ouvir.',
  },
  {
    key: 'ansioso',
    emoji: '😰',
    label: 'Ansioso',
    msg: 'Respira fundo 💛 Seus pais estão aqui pra te ajudar.',
  },
];
const byKey = (k: MoodKey) => MOODS.find((m) => m.key === k) ?? MOODS[0];

export function MoodCheckIn() {
  const [today, setToday] = useState<MoodKey | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<MoodKey | null>(null);

  useEffect(() => {
    let alive = true;
    getTodayMood().then((res) => {
      if (!alive) return;
      const m = res.success && res.data ? res.data.today?.mood ?? null : null;
      setToday(m);
      setOpen(m === null); // ainda não marcou → abre o prompt
    });
    return () => {
      alive = false;
    };
  }, []);

  const pick = useCallback((key: MoodKey) => {
    setToday(key);
    setConfirm(key);
    void apiSetMood(key);
    setTimeout(() => {
      setConfirm(null);
      setOpen(false);
    }, 2600);
  }, []);

  if (today === undefined) return null; // carregando: nada (evita flash)

  // Confirmação (logo após escolher)
  if (confirm) {
    const m = byKey(confirm);
    return (
      <div className={`${styles.card} ${styles.confirmCard}`}>
        <span className={styles.confirmEmoji}>{m.emoji}</span>
        <p className={styles.confirmText}>{m.msg}</p>
      </div>
    );
  }

  // Chip (já respondeu e está fechado)
  if (today && !open) {
    const m = byKey(today);
    return (
      <button
        className={styles.chip}
        onClick={() => setOpen(true)}
        title="Trocar o humor de hoje"
      >
        <span className={styles.chipEmoji}>{m.emoji}</span>
        <span>
          Hoje você tá: <strong>{m.label}</strong>
        </span>
        <span className={styles.chipEdit}>trocar</span>
      </button>
    );
  }

  // Prompt (picker)
  return (
    <div className={`${styles.card} ${today === null ? styles.pulse : ''}`}>
      <div className={styles.head}>
        <span className={styles.title}>Como você tá hoje? 💛</span>
        {today && (
          <button className={styles.close} onClick={() => setOpen(false)} aria-label="Fechar">
            ×
          </button>
        )}
      </div>
      <div className={styles.moods}>
        {MOODS.map((m, i) => (
          <button
            key={m.key}
            className={`${styles.mood} ${today === m.key ? styles.moodActive : ''}`}
            style={{ animationDelay: `${i * 45}ms` }}
            onClick={() => pick(m.key)}
            aria-label={m.label}
            title={m.label}
          >
            <span className={styles.moodEmoji}>{m.emoji}</span>
            <span className={styles.moodLabel}>{m.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
